import React, { useState } from 'react';
import Modal from '../common/Modal';
import TextAreaField from '../common/TextAreaField';
import { superAdminApi } from '../../services/superAdminApi';

const RejectionModal = ({ request, onClose, onComplete }) => {
  const [formData, setFormData] = useState({
    rejection_reason: '',
    admin_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const commonReasons = [
    'Incomplete application information',
    'Organization does not meet eligibility criteria',
    'Duplicate application',
    'Invalid contact information',
    'Insufficient documentation',
    'Organization already using similar platform',
    'Application does not align with platform requirements'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      await superAdminApi.rejectSignupRequest(request.id, formData);
      onComplete();
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors || {});
      } else {
        setErrors({ 
          general: error.response?.data?.message || 'Failed to reject request' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const selectCommonReason = (reason) => {
    setFormData({ ...formData, rejection_reason: reason });
    if (errors.rejection_reason) {
      setErrors({ ...errors, rejection_reason: null });
    }
  };

  return (
    <Modal onClose={onClose} title="Reject Signup Request" size="large">
      <div className="rejection-modal">
        <div className="request-summary">
          <h3>Request Details</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <label>Organization:</label>
              <span>{request.organization_name}</span>
            </div>
            <div className="summary-item">
              <label>Contact Person:</label>
              <span>{request.contact_person}</span>
            </div>
            <div className="summary-item">
              <label>Email:</label>
              <span>{request.email}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="rejection-form">
          <div className="warning-message">
            <i className="fas fa-exclamation-triangle"></i>
            <div>
              <strong>This action will reject the signup request.</strong>
              <p>The applicant will be notified via email with the reason for rejection.</p>
            </div>
          </div>

          {errors.general && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {errors.general}
            </div>
          )}

          <div className="common-reasons">
            <h4>Common Rejection Reasons:</h4>
            <div className="reason-buttons">
              {commonReasons.map((reason, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => selectCommonReason(reason)}
                  className={`reason-button ${formData.rejection_reason === reason ? 'selected' : ''}`}
                >
                  {reason}
                </button>
              ))}
            </div>
          </div>

          <TextAreaField
            label="Rejection Reason *"
            value={formData.rejection_reason}
            onChange={(value) => handleChange('rejection_reason', value)}
            placeholder="Please provide a clear reason for rejecting this request..."
            rows={4}
            error={errors.rejection_reason?.[0]}
            required
            fullWidth
          />

          <TextAreaField
            label="Internal Admin Notes"
            value={formData.admin_notes}
            onChange={(value) => handleChange('admin_notes', value)}
            placeholder="Add any internal notes about this rejection (not sent to applicant)..."
            rows={3}
            error={errors.admin_notes?.[0]}
            fullWidth
          />

          <div className="form-actions">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading} 
              className="btn-danger"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Rejecting...
                </>
              ) : (
                <>
                  <i className="fas fa-times"></i>
                  Reject Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RejectionModal;